import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

import { API } from '@/utils/apiConfig';

const defaultTheme = {
  primary_color: '#16a34a',
  primary_hover: '#15803d',
  secondary_color: '#facc15',
  secondary_hover: '#eab308',
  accent_color: '#ca8a04',
  topbar_bg: '#16a34a',
  topbar_text: '#ffffff',
  header_bg: '#ffffff',
  menu_text: '#374151',
  menu_active_bg: '#facc15',
  menu_active_text: '#1f2937',
  hero_overlay_color: '#facc15',
  hero_overlay_opacity: 0.7,
  hero_slogan: 'SOLAR GREEN S.R.O.',
  hero_subtitle: 'Jsme ryze česká společnost, která se zabývá projektováním, montáží a servisem systémů využívajících obnovitelných zdrojů energie.',
  text_primary: '#1f2937',
  text_secondary: '#6b7280',
  font_heading: 'Manrope',
  font_body: 'DM Sans',
  footer_bg: '#1e293b',
  footer_text: '#ffffff',
};

const ThemeContext = createContext({
  theme: defaultTheme,
  loading: true,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const response = await axios.get(`${API}/settings/theme`);
      const fetchedTheme = { ...defaultTheme, ...response.data };
      setTheme(fetchedTheme);
      applyTheme(fetchedTheme);
    } catch (error) {
      console.error('Error fetching theme:', error);
      applyTheme(defaultTheme);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (t) => {
    const root = document.documentElement;
    
    // Set CSS variables
    root.style.setProperty('--color-primary', t.primary_color);
    root.style.setProperty('--color-primary-hover', t.primary_hover);
    root.style.setProperty('--color-secondary', t.secondary_color);
    root.style.setProperty('--color-secondary-hover', t.secondary_hover);
    root.style.setProperty('--color-accent', t.accent_color);
    root.style.setProperty('--color-topbar-bg', t.topbar_bg);
    root.style.setProperty('--color-topbar-text', t.topbar_text);
    root.style.setProperty('--color-header-bg', t.header_bg);
    root.style.setProperty('--color-menu-text', t.menu_text);
    root.style.setProperty('--color-menu-active-bg', t.menu_active_bg);
    root.style.setProperty('--color-menu-active-text', t.menu_active_text);
    root.style.setProperty('--color-hero-overlay', t.hero_overlay_color);
    root.style.setProperty('--hero-overlay-opacity', t.hero_overlay_opacity);
    root.style.setProperty('--color-text-primary', t.text_primary);
    root.style.setProperty('--color-text-secondary', t.text_secondary);
    root.style.setProperty('--font-heading', t.font_heading);
    root.style.setProperty('--font-body', t.font_body);
    root.style.setProperty('--color-footer-bg', t.footer_bg);
    root.style.setProperty('--color-footer-text', t.footer_text);

    // Load Google Fonts
    const fonts = [t.font_heading, t.font_body].filter((v, i, a) => a.indexOf(v) === i);
    const fontUrl = `https://fonts.googleapis.com/css2?family=${fonts.map(f => f.replace(' ', '+')).join('&family=')}&display=swap`;
    
    let fontLink = document.getElementById('theme-fonts');
    if (!fontLink) {
      fontLink = document.createElement('link');
      fontLink.id = 'theme-fonts';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
    fontLink.href = fontUrl;
  };

  return (
    <ThemeContext.Provider value={{ theme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
